import React, { useState, Dispatch, FormEvent, SetStateAction } from 'react';

import { PersonalInformation } from '../../types/PersonalInformation';
import EditFamilyMember from './EditFamilyMember';
import FamilyMembers from './FamilyMembers';
import globalStyles from '../../App.module.scss';
import familyStyles from './familyInformationPage.module.scss';
import { Medlemskapstype } from '../../api/medlemskapAPI';
import { MemberInput } from '../../types/MemberInput';

import {
    isValid,
    isValidOrEmptyPartner,
    isValidOrEmptyBarneList,
    validateAddress,
    validateDateOfBirth,
    validateDateOfBirthAndChildAge,
    validateEmail,
    validateFirstname,
    validateLastname,
    validatePhoneNumber,
    validatePostalcode,
} from '../validation';
import TermsLink from '../TermsLink';
import PaymentSummaryWithDiscount from '../../PaymentSummary/PaymentSummaryWithDiscount';
import { ApiResource } from '../../api/ApiResource';
import { PostalCodeResponse } from '../../api/postalCodeAPI';

function FamilyInformationForm(props: {
    postalCodesListResource: ApiResource<PostalCodeResponse>;
    activeMedlemskap?: Medlemskapstype;
    memberInput: MemberInput;
    setHovedmedlemInput: Dispatch<SetStateAction<PersonalInformation>>;
    setPartnerInput: Dispatch<SetStateAction<PersonalInformation | undefined>>;
    setBarnInput: Dispatch<SetStateAction<PersonalInformation[] | undefined>>;
    discountCode: string;
    setDiscountCode: Dispatch<SetStateAction<string>>;
    discount: number;
    setDiscount: Dispatch<SetStateAction<number>>;
    onSubmit: () => void;
}) {
    const [formErrorsHovedmedlem, setFormErrorsHovedmedlem] = useState<PersonalInformation>({
        phoneNumber: '',
        firstname: '',
        lastname: '',
        email: '',
        address: '',
        postalcode: '',
        dateOfBirth: '',
    });

    const [formErrorsPartner, setFormErrorsPartner] = useState<PersonalInformation>({
        phoneNumber: '',
        firstname: '',
        lastname: '',
        email: '',
        address: '',
        postalcode: '',
        dateOfBirth: '',
    });

    const [formErrorsBarn, setFormErrorsBarn] = useState<PersonalInformation[]>([]);

    const setAllValidationErrors = () => {
        setFormErrorsHovedmedlem({
            firstname: validateFirstname(props.memberInput.hovedmedlem.firstname),
            lastname: validateLastname(props.memberInput.hovedmedlem.lastname),
            phoneNumber: validatePhoneNumber(props.memberInput.hovedmedlem.phoneNumber),
            email: validateEmail(props.memberInput.hovedmedlem.email),
            address: validateAddress(props.memberInput.hovedmedlem.address),
            postalcode: validatePostalcode(props.memberInput.hovedmedlem.postalcode),
            dateOfBirth: validateDateOfBirth(props.memberInput.hovedmedlem.dateOfBirth),
        });
        if (props.memberInput.partner) {
            setFormErrorsPartner({
                firstname: validateFirstname(props.memberInput.partner.firstname),
                lastname: validateLastname(props.memberInput.partner.lastname),
                phoneNumber: validatePhoneNumber(props.memberInput.partner.phoneNumber),
                email: validateEmail(props.memberInput.partner.email),
                address: '',
                postalcode: '',
                dateOfBirth: validateDateOfBirth(props.memberInput.partner.dateOfBirth),
            });
        }
        if (props.memberInput.barn) {
            let errorList: PersonalInformation[] = [];
            props.memberInput.barn.forEach((child) => {
                errorList.push({
                    firstname: validateFirstname(child.firstname),
                    lastname: validateLastname(child.lastname),
                    phoneNumber: validatePhoneNumber(child.phoneNumber),
                    email: validateEmail(child.email),
                    address: '',
                    postalcode: '',
                    dateOfBirth: validateDateOfBirthAndChildAge(child.dateOfBirth),
                });
            });
            setFormErrorsBarn(errorList);
        }
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (
            isValid(props.memberInput.hovedmedlem) &&
            isValidOrEmptyPartner(props.memberInput.partner) &&
            isValidOrEmptyBarneList(props.memberInput.barn)
        ) {
            props.onSubmit();
        } else {
            setAllValidationErrors();
        }
    };

    const hovedMedlemIsSet = isValid(props.memberInput.hovedmedlem);

    return (
        <div className={familyStyles.formContainer}>
            <form onSubmit={handleSubmit} noValidate={true}>
                {!hovedMedlemIsSet ? (
                    <EditFamilyMember
                        postalCodesListResource={props.postalCodesListResource}
                        isHovedmedlem={true}
                        formInput={props.memberInput.hovedmedlem}
                        setFormInput={props.setHovedmedlemInput}
                    />
                ) : (
                    <React.Fragment>
                        <FamilyMembers
                            familyMembers={props.memberInput}
                            setHovedmedlemInput={props.setHovedmedlemInput}
                            setPartnerInput={props.setPartnerInput}
                            setBarnInput={props.setBarnInput}
                            formErrorsHovedmedlem={formErrorsHovedmedlem}
                            formErrorsPartner={formErrorsPartner}
                            formErrorsBarn={formErrorsBarn}
                        />

                        <div className={globalStyles.summary}>
                            <PaymentSummaryWithDiscount
                                medlemskapstype={props.activeMedlemskap}
                                discount={props.discount}
                                setDiscount={props.setDiscount}
                                discountCode={props.discountCode}
                                setDiscountCode={props.setDiscountCode}
                            />
                            <TermsLink />
                            <button type={'submit'} className={globalStyles.standardButton}>
                                Til betaling
                            </button>
                        </div>
                        <p className={globalStyles.smallInfoText}>
                            Les{' '}
                            <a href="https://www.skiforeningen.no/omoss/personvern/">
                                Skiforeningens personvernerklæring
                            </a>
                            .
                        </p>
                    </React.Fragment>
                )}
            </form>
        </div>
    );
}

export default FamilyInformationForm;
